// SelectPlayer/index.tsx
import React from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { useTRPC } from "@/trpc";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { useDebounce } from "@uidotdev/usehooks";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useVirtualizer } from "@tanstack/react-virtual";

interface Props {
  value?: string;
  onValueChange?: (value: string | undefined) => void;
  searchOptions?: {
    competition?: {
      country?: string;
      competition_id?: string;
      club_id?: string;
    },
    playerData?: {
      club?: string[];
    }
  }
}

const SelectPlayer = ({ value, onValueChange, searchOptions }: Props) => {
  const { trpc } = useTRPC();
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const debouncedSearch = useDebounce(search, 500);

  const [selectedValue, setSelectedValue] = React.useState<string | undefined>(value);
  React.useEffect(() => setSelectedValue(value), [value]);

  const optionsKey = React.useMemo(() => JSON.stringify(searchOptions ?? {}), [searchOptions]);
  const prevOptionsKeyRef = React.useRef(optionsKey);
  const optionsChangedRef = React.useRef(false);
  React.useEffect(() => {
    optionsChangedRef.current = optionsKey !== prevOptionsKeyRef.current;
    prevOptionsKeyRef.current = optionsKey;
  }, [optionsKey]);

  const queryKey = React.useMemo(
    () => ["players", debouncedSearch || "", optionsKey, "all"],
    [debouncedSearch, optionsKey]
  );

  const { data, isFetching, isError, error } = useQuery({
    queryKey,
    enabled: open,
    keepPreviousData: true,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      trpc.players.search.query({
        search: debouncedSearch || undefined,
        all: true,
        ...searchOptions,
      }),
  });

  React.useEffect(() => {
    if (isError && error instanceof Error) toast.error(error.message);
  }, [isError, error]);

  React.useEffect(() => {
    if (!open || isFetching || selectedValue === undefined || !optionsChangedRef.current) return;
    const exists = data?.players?.some((u: any) => String(u.id) === String(selectedValue));
    if (!exists) {
      setSelectedValue(undefined);
      onValueChange?.(undefined);
    }
    optionsChangedRef.current = false;
  }, [open, isFetching, data, selectedValue, onValueChange]);

  const handleSelect = React.useCallback(
    (id: string | undefined) => {
      if (selectedValue === id) {
        setSelectedValue(undefined);
        onValueChange?.(undefined);
      } else {
        setSelectedValue(id);
        onValueChange?.(id);
      }
      setOpen(false);
    },
    [selectedValue, onValueChange]
  );

  const selectedPlayer = data?.players?.find((u: any) => String(u.id) === String(selectedValue));
  const selectedLabel = selectedPlayer?.name ?? undefined;

  const rows = data?.players ?? [];
  const parentRef = React.useRef<HTMLDivElement | null>(null);
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 56,
    overscan: 8,
  });

  React.useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => rowVirtualizer.measure());
    return () => cancelAnimationFrame(id);
  }, [open, rowVirtualizer]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full">
          {selectedLabel || "Select player..."}
        </Button>
      </DialogTrigger>

      <DialogContent className="p-0 rounded-3xl gap-0 overflow-hidden" hideCloseButton>
        <div className="flex items-center relative">
          <input
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder="Search players"
            className="focus:outline-none py-3 px-4 bg-input/50 rounded-3xl text-sm w-full h-12"
          />
          <button
            onClick={() => setOpen(false)}
            className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground size-12 aspect-square hover:bg-muted rounded-full flex items-center justify-center"
          >
            <Icons.X className="w-4 h-4" />
          </button>
        </div>

        {isFetching && <p className="text-center text-sm text-muted-foreground my-2">Searching...</p>}

        <div ref={parentRef} className="max-h-96 overflow-auto relative">
          <div style={{ height: rowVirtualizer.getTotalSize(), position: "relative" }} className="w-full">
            {rowVirtualizer.getVirtualItems().map((v) => {
              const player = rows[v.index];
              const isSelected = String(player?.id) === String(selectedValue);
              return (
                <div
                  key={player.id}
                  data-index={v.index}
                  className="absolute top-0 left-0 w-full"
                  style={{ transform: `translateY(${v.start}px)` }}
                >
                  <button
                    onClick={() => handleSelect(player.id)}
                    className={`flex items-center justify-between gap-4 p-2 rounded-xl hover:bg-muted text-left ${
                      isSelected ? "bg-muted/60" : ""
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="size-8 aspect-square relative">
                        <div className="h-full w-full bg-foreground/10 rounded-full flex items-center justify-center">
                          <Avatar className="w-full h-full">
                            <AvatarImage className="object-cover" src={player?.imageUrl ?? undefined} alt="" />
                            <AvatarFallback className="bg-foreground/10 opacity-50">
                              {player?.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                        </div>
                      </div>
                      <div>
                        <p className="font-medium">{player?.name}</p>
                      </div>
                    </div>

                    {isSelected && <Icons.Check className="w-4 h-4 text-foreground" />}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SelectPlayer;
